/**
 * The server-side admin allowlist — FR-AUTH-04, `ARCHITECTURE.md` § 5.3, `BOUNDARIES.md` § 5.2.
 *
 * `role = allowlist(normalizeEmail(token.email)) ? admin : student`, re-derived on **every** call.
 * The `role` field stored on an account document is a display cache and is never an authority, and a
 * token claim never grants admin. Adding an admin is therefore a deployment change to this file, not
 * a data change.
 *
 * The list is exactly the two MVP accounts named in the approved SPEC (FR-AUTH-04) and nothing else.
 * It is not read from the environment, from Firestore, from Simple QA v1, or from any external
 * account state: a value that can be edited at runtime is not an allowlist.
 */
import { z } from 'zod';

/**
 * Frozen so the array cannot be extended at runtime. `as const` gives the exact tuple type; the
 * freeze makes `push`/index assignment throw in module (strict-mode) code instead of silently
 * granting admin.
 */
export const ADMIN_EMAILS = Object.freeze(['g@akarion.ge', 'me@mrnutrition.academy'] as const);

export type AdminEmail = (typeof ADMIN_EMAILS)[number];

export const adminEmailSchema = z.enum(ADMIN_EMAILS);

/**
 * The single normalization rule for every email in the system — `data-model.md` § Account:
 * "trimmed + lower-cased full address; exact-match key".
 *
 * - `trim()` removes Unicode whitespace (including a pasted NBSP and a leading BOM) from both ends.
 * - `toLowerCase()` is the locale-independent form. `toLocaleLowerCase()` is deliberately not used:
 *   under a Turkish locale it maps `I` to `ı`, which would silently change an address.
 * - Nothing else is rewritten. Interior whitespace is left in place (it makes the address invalid,
 *   which is the caller's validation problem, not something to hide), and `+tag` / `.` forms are
 *   **not** collapsed, because the normalized address is an exact-match key: `g+admin@akarion.ge`
 *   is a different key from `g@akarion.ge` and must not resolve to an admin.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Exact membership of the normalized address in the allowlist. Anything else — a substring, a
 * different domain, a `+tag` variant, an empty or whitespace-only input — is a student.
 */
export function isAdminEmail(raw: string): boolean {
  return (ADMIN_EMAILS as readonly string[]).includes(normalizeEmail(raw));
}
